import type { TriageEntry } from '../order'
import type { EntityId } from '../shared'

export const BULK_ACTION_MAX_ORDER_COUNT = 100
export const BULK_ACTION_MAX_STEP_COUNT = 2
export const BULK_ACTION_HISTORY_PAGE_SIZE = 10

export const BULK_ACTION_TYPES = ['triage', 'reroute', 'resolve_triage'] as const
export type BulkActionType = (typeof BULK_ACTION_TYPES)[number]

export const BULK_ACTION_SOURCE_QUEUES = ['Triage', 'PV1', 'PV2', 'Packing', 'Fill'] as const
export type BulkActionSourceQueue = (typeof BULK_ACTION_SOURCE_QUEUES)[number]

export const BULK_ACTION_STEP_RESULT_STATUSES = ['success', 'failed', 'skipped'] as const
export type BulkActionStepResultStatus = (typeof BULK_ACTION_STEP_RESULT_STATUSES)[number]

export type BulkActionStepResult = {
  orderId: EntityId
  status: BulkActionStepResultStatus
  error?: string
  processedAt: Date
}

export type BulkTriageEntry = TriageEntry & { details?: { dateOkToFill?: Date } }

export type RerouteParams = { locationId: string }
export type TriageParams = { entries: BulkTriageEntry[] }
export type ResolveTriageParams = { reason: string }
export type BulkActionParams = RerouteParams | TriageParams | ResolveTriageParams

export type BulkActionStep = {
  type: BulkActionType
  params: BulkActionParams
  results: BulkActionStepResult[]
}

export type CreateBulkActionStepArgs = {
  type: BulkActionType
  triageParams?: TriageParams
  rerouteParams?: RerouteParams
  resolveTriageParams?: ResolveTriageParams
}

export const BULK_ACTIONS: Record<
  BulkActionType,
  { label: string; queues: BulkActionSourceQueue[]; otcOnlyQueues?: BulkActionSourceQueue[] }
> = {
  triage: {
    label: 'Triage',
    queues: ['Triage', 'PV1', 'PV2', 'Packing', 'Fill'],
  },
  reroute: {
    label: 'Reroute',
    queues: ['Triage', 'PV1', 'Fill'],
    otcOnlyQueues: ['Packing'],
  },
  resolve_triage: {
    label: 'Resolve Triage',
    queues: ['Triage'],
  },
}

// Whether a bulk action of `type` may run from `sourceQueue`. Queues in `otcOnlyQueues` are only
// permitted when every targeted order is OTC-only.
export const isBulkActionAllowedFromQueue = (
  type: BulkActionType,
  sourceQueue: BulkActionSourceQueue,
  isOtcOnly = false,
): boolean => {
  const action = BULK_ACTIONS[type]
  if (action.queues.includes(sourceQueue)) return true
  return isOtcOnly && !!action.otcOnlyQueues?.includes(sourceQueue)
}
